import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { ArrowCircle, ArrowCircleProps } from 'components/ArrowCircle';
import styles from './adInfo.scss';
import { RedAd } from 'models/RedAd';
import cn from 'classnames';
import connect from 'components/Shared/connect';
import { isMobile, isMobileLayout, isTablet } from 'components/Shared/responsive';
import { ThemeMeta } from 'models/SectionBlock';
import { BhImage } from 'models/BhImage';
import Image from 'components/Shared/Image';
import { NO_ELEMENT } from 'utils';
import { useSelector } from 'react-redux';

type Size = 'large' | 'small';
interface SizeProps {
  size?: Size;
}

interface BaseProps {
  children?: ReactNode;
  className?: string;
}

interface ImageDimension {
  width: number;
  height: number;
}

interface AdInfoProps extends BaseProps, SizeProps, ThemeMeta {
  transparent?: boolean;
  background?: BhImage;
  backgroundType?: string;
  backgroundDimension?: ImageDimension;
  themeSplash?: BhImage;
  themeSplashDimension?: ImageDimension;
  themeSplashClassName?: string;
  triangle?: boolean;
}

const defaultDimensions = {
  width: 525,
  height: 450
};

type Align = 'left' | 'right' | 'center';
interface AlignProps {
  align?: Align;
}

const Triangle = () => {
  return <div className={styles.triangle} />;
};

const useResize = (ref: RefObject<HTMLDivElement>) => {
  const [size, setSize] = useState(0);

  useEffect(() => {
    const resize = () => {
      const boundingClientRect = ref?.current?.getBoundingClientRect();
      if (boundingClientRect) {
        setSize(boundingClientRect.height);
      }
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [ref]);

  return size;
};

export const AdInfo = ({
  children,
  size = 'small',
  className,
  transparent,
  onSurface,
  background,
  backgroundType,
  backgroundDimension,
  themeSplash,
  themeSplashDimension,
  themeSplashClassName,
  triangle
}: AdInfoProps) => {
  const ref = useRef(NO_ELEMENT);
  const triangleSize = useResize(ref);
  const shouldRender = triangle ? triangleSize > 0 : true;
  const mobileLayout = useSelector(isMobile);

  const transparentBlock = background ? true : transparent;
  return (
    <div
      ref={ref}
      style={{ backgroundColor: onSurface }}
      className={cn(
        styles.adInfo,
        { [styles.large]: size === 'large' },
        { [styles.transparent]: transparentBlock },
        { [styles.hide]: !shouldRender },
        className
      )}
    >
      {background && (
        <Image
          {...background}
          {...(backgroundDimension || defaultDimensions)}
          className={cn(
            styles.themedBanner,
            { [styles.panoramaSizeTheme]: backgroundType === 'panorama' },
            { [styles.quarterSizeTheme]: backgroundType === 'quarter' }
          )}
        />
      )}
      {triangle && !mobileLayout && <Triangle />}
      {themeSplash && (
        <div
          className={cn(styles.themeSplashWrapper, {
            [styles.themeSplashWrapperPanorama]: backgroundType === 'panorama'
          })}
        >
          <Image
            {...themeSplash}
            className={themeSplashClassName}
            {...(themeSplashDimension || defaultDimensions)}
          />
        </div>
      )}
      {children}
    </div>
  );
};

type AdPreTitleProps = BaseProps & SizeProps;
export const AdPreTitle = ({ children, size = 'small', className }: AdPreTitleProps) => (
  <h5 className={cn(styles.preTitle, { [styles.large]: size === 'large' }, className)}>
    {children}
  </h5>
);

type AdTitleProps = BaseProps & SizeProps & AlignProps;
export const AdTitle = ({ children, size = 'small', align = 'left', className }: AdTitleProps) => (
  <h4
    className={cn(
      styles.title,
      { [styles.large]: size === 'large' },
      { [styles.titleCenter]: align === 'center' },
      { [styles.titleRight]: align === 'right' },
      className
    )}
  >
    {children}
  </h4>
);

type AdTextProps = BaseProps & SizeProps;
export const AdText = ({ children, size = 'small', className }: AdTextProps) => (
  <p className={cn(styles.text, { [styles.large]: size === 'large' }, className)}>{children}</p>
);

type AdButtonProps = Pick<RedAd, 'url' | 'ticket'> & Pick<BaseProps, 'className'>;
type AdButtonMergedProps = AdButtonProps & Pick<ArrowCircleProps, 'size'>;
const adButton = ({ size, className }: AdButtonMergedProps) => (
  <div className={cn(styles.button, className)}>
    <ArrowCircle className={styles.circle} size={size} />
  </div>
);

interface StateProps {
  isMobile: boolean;
  isTablet: boolean;
}
export const AdButtonPanorama = connect<AdButtonMergedProps, AdButtonProps, StateProps>(
  state => ({
    isMobile: isMobile(state),
    isTablet: isTablet(state)
  }),
  undefined,
  ({ isMobile, isTablet }, dispatch, props) => ({
    ...dispatch,
    ...props,
    size: isMobile ? 'small' : isTablet ? 'medium' : 'large'
  })
)(adButton);

export const AdButton = connect<AdButtonMergedProps, AdButtonProps>(state => ({
  size: isMobileLayout(state) ? 'medium' : 'large'
}))(adButton);
